import * as XLSX from 'xlsx';
import { OptimizationResults, OptimizationResultsWithStats } from '../types';
import { HistoryEntry } from './historyStorage';
import { calculateCombinedWinner } from './combinedWinnerUtils';

/**
 * Helper function to create box plot sheet for any metric
 */
function createBoxPlotDataSheet(
  statistics: any,
  metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
  algorithms: string[],
  algorithmKeys: readonly string[]
) {
  const header: (string | number)[] = ['Algorithm', 'Min', 'Q1 (25%)', 'Median (50%)', 'Q3 (75%)', 'Max', 'Mean', 'Std Dev', 'IQR', 'Range'];
  const data: (string | number)[][] = [header];

  algorithmKeys.forEach((key, idx) => {
    const stats = statistics[key][metricKey];
    // Calculate quartiles (approximation using available statistics)
    const q1 = (stats.min + stats.median) / 2;
    const q3 = (stats.median + stats.max) / 2;
    const iqr = q3 - q1;
    const range = stats.max - stats.min;
    
    data.push([
      algorithms[idx],
      stats.min,
      q1,
      stats.median,
      q3,
      stats.max,
      stats.mean,
      stats.stdDev,
      iqr,
      range,
    ]);
  });

  const sheet = XLSX.utils.aoa_to_sheet(data);
  
  // Set column widths
  sheet['!cols'] = [
    { wch: 12 }, // Algorithm
    { wch: 12 }, // Min
    { wch: 12 }, // Q1
    { wch: 12 }, // Median
    { wch: 12 }, // Q3
    { wch: 12 }, // Max
    { wch: 12 }, // Mean
    { wch: 12 }, // Std Dev
    { wch: 12 }, // IQR
    { wch: 12 }, // Range
  ];

  return sheet;
}

/**
 * Helper function to add box plot sheets to any workbook when statistics are available
 */
function addBoxPlotSheetsIfAvailable(
  workbook: XLSX.WorkBook,
  results: OptimizationResults | OptimizationResultsWithStats
): void {
  // Check if statistics are available
  const hasStatistics = 'statistics' in results && results.statistics !== undefined;
  if (!hasStatistics || !results.statistics) {
    return; // No statistics, skip adding box plot sheets
  }

  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;

  // Add box plot sheets for each metric
  const energyBoxPlot = createBoxPlotDataSheet(results.statistics, 'energy', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, energyBoxPlot, 'Energy Box Plot');

  const fitnessBoxPlot = createBoxPlotDataSheet(results.statistics, 'fitness', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, fitnessBoxPlot, 'Fitness Box Plot');

  const costBoxPlot = createBoxPlotDataSheet(results.statistics, 'cost', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, costBoxPlot, 'Cost Box Plot');

  const responseTimeBoxPlot = createBoxPlotDataSheet(results.statistics, 'responseTime', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, responseTimeBoxPlot, 'Response Time Box Plot');
}

/**
 * Generates analysis report text from optimization results
 */
export function generateAnalysisText(results: OptimizationResults): string {
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;

  const lines: string[] = [];
  lines.push('='.repeat(80));
  lines.push('ANALYSIS RESULTS');
  lines.push('='.repeat(80));
  lines.push(`Configuration: Tasks=${results.configuration.taskCount}, Properties=${results.configuration.propertyCount}, Population=${results.configuration.initialPopulation}`);
  lines.push('');

  // Fitness comparison
  lines.push('1. FITNESS COMPARISON (Higher is Better)');
  lines.push('-'.repeat(80));
  const fitnessData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    fitness: results[key].fitness,
  })).sort((a, b) => b.fitness - a.fitness);
  lines.push(`Winner: ${fitnessData[0].name} with Fitness = ${fitnessData[0].fitness.toFixed(6)}`);
  lines.push('');
  lines.push('Ranking:');
  fitnessData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.fitness.toFixed(6)}`);
  });
  lines.push('');

  // Energy comparison
  lines.push('2. ENERGY CONSUMPTION COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const energyData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    energy: results[key].energy,
  })).sort((a, b) => a.energy - b.energy);
  lines.push(`Winner: ${energyData[0].name} with Energy = ${energyData[0].energy.toFixed(6)} Joules`);
  lines.push('');
  lines.push('Ranking:');
  energyData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.energy.toFixed(6)} Joules`);
  });
  lines.push('');

  // Response Time comparison
  lines.push('3. RESPONSE TIME COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const responseTimeData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    responseTime: results[key].responseTime,
  })).sort((a, b) => a.responseTime - b.responseTime);
  lines.push(`Winner: ${responseTimeData[0].name} with Response Time = ${responseTimeData[0].responseTime.toFixed(2)} ms`);
  lines.push('');
  lines.push('Ranking:');
  responseTimeData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.responseTime.toFixed(2)} ms`);
  });
  lines.push('');

  // Execution Cost comparison
  lines.push('4. EXECUTION COST COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const costData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    cost: results[key].cost,
  })).sort((a, b) => a.cost - b.cost);
  lines.push(`Winner: ${costData[0].name} with Cost = ${costData[0].cost.toFixed(6)}`);
  lines.push('');
  lines.push('Ranking:');
  costData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.cost.toFixed(6)}`);
  });
  lines.push('');

  // Combined Winner (Best Solution + Lowest Energy)
  lines.push('5. COMBINED WINNER (Best Solution + Lowest Energy)');
  lines.push('-'.repeat(80));
  const combinedWinner = calculateCombinedWinner(results);
  if (combinedWinner) {
    if (combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0) {
      lines.push('Perfect Winner(s) - #1 in BOTH Fitness AND Energy:');
      combinedWinner.perfectWinners.forEach(winner => {
        lines.push(`  ${winner.name.padEnd(10)} : Fitness = ${winner.fitness.toFixed(6)} (#1), Energy = ${winner.energy.toFixed(6)} J (#1)`);
      });
    } else if (combinedWinner.bestAlternative) {
      lines.push('Best Alternative (No algorithm wins both metrics):');
      const alt = combinedWinner.bestAlternative;
      const fitnessStatus = alt.fitness === combinedWinner.bestFitness ? '#1 - Best Solution' : `Rank #${alt.fitnessRank}`;
      const energyStatus = alt.energy === combinedWinner.bestEnergy ? '#1 - Lowest Energy' : `Rank #${alt.energyRank}`;
      lines.push(`  ${alt.name.padEnd(10)} : Fitness = ${alt.fitness.toFixed(6)} (${fitnessStatus}), Energy = ${alt.energy.toFixed(6)} J (${energyStatus})`);
    }
    lines.push('');
    lines.push('Combined Ranking:');
    combinedWinner.rankedAlgorithms.forEach((alg, idx) => {
      const status = alg.isPerfectWinner ? ' [PERFECT WINNER]' : alg.isBestAlternative ? ' [BEST ALTERNATIVE]' : '';
      lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : Fitness Rank #${alg.fitnessRank}, Energy Rank #${alg.energyRank}${status}`);
    });
  } else {
    lines.push('No combined winner data available.');
  }
  lines.push('');

  return lines.join('\n');
}

/**
 * Exports optimization results to Excel file
 */
export function exportToExcel(results: OptimizationResults, filename: string = 'optimization_results.xlsx'): void {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Fitness Results
  const fitnessData = [
    ['Algorithm', 'Fitness', 'Energy (Joules)', 'Cost', 'Response Time (ms)'],
    ['GA', results.ga.fitness, results.ga.energy, results.ga.cost, results.ga.responseTime],
    ['Cultural', results.culture.fitness, results.culture.energy, results.culture.cost, results.culture.responseTime],
    ['SFLA', results.frog.fitness, results.frog.energy, results.frog.cost, results.frog.responseTime],
    ['SFLAGA', results.sflaga.fitness, results.sflaga.energy, results.sflaga.cost, results.sflaga.responseTime],
    ['PSO', results.pso.fitness, results.pso.energy, results.pso.cost, results.pso.responseTime],
    ['GAPSO', results.gapso.fitness, results.gapso.energy, results.gapso.cost, results.gapso.responseTime],
  ];
  const fitnessSheet = XLSX.utils.aoa_to_sheet(fitnessData);
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Results');

  // Sheet 2: Solutions
  const solutionsData: (string | number)[][] = [
    ['Algorithm', ...Array.from({ length: results.ga.solution.length }, (_, i) => `Task ${i + 1}`)],
  ];
  
  const algorithms = [
    { name: 'GA', solution: results.ga.solution },
    { name: 'Cultural', solution: results.culture.solution },
    { name: 'SFLA', solution: results.frog.solution },
    { name: 'SFLAGA', solution: results.sflaga.solution },
    { name: 'PSO', solution: results.pso.solution },
    { name: 'GAPSO', solution: results.gapso.solution },
  ];

  algorithms.forEach(({ name, solution }) => {
    solutionsData.push([name, ...solution]);
  });

  const solutionsSheet = XLSX.utils.aoa_to_sheet(solutionsData);
  XLSX.utils.book_append_sheet(workbook, solutionsSheet, 'Solutions');

  // Sheet 3: Configuration
  const configData = [
    ['Parameter', 'Value'],
    ['Task Count', results.configuration.taskCount],
    ['Property Count', results.configuration.propertyCount],
    ['Initial Population', results.configuration.initialPopulation],
    ['Crossover Rate', results.configuration.crossover],
    ['Mutation Rate', results.configuration.mutation],
  ];
  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration');

  // Sheet 4: Combined Winner
  const combinedWinner = calculateCombinedWinner(results);
  const combinedWinnerData: (string | number)[][] = [
    ['Combined Winner Analysis'],
    ['Best Solution (Fitness) + Lowest Energy'],
    [''],
  ];
  
  if (combinedWinner) {
    if (combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0) {
      combinedWinnerData.push(['Perfect Winner(s) - #1 in BOTH Fitness AND Energy:']);
      combinedWinner.perfectWinners.forEach(winner => {
        combinedWinnerData.push([
          winner.name,
          winner.fitness,
          winner.energy,
          'Perfect Winner'
        ]);
      });
    } else if (combinedWinner.bestAlternative) {
      combinedWinnerData.push(['Best Alternative (No algorithm wins both metrics):']);
      const alt = combinedWinner.bestAlternative;
      combinedWinnerData.push([
        alt.name,
        alt.fitness,
        alt.energy,
        'Best Alternative'
      ]);
    }
    combinedWinnerData.push(['']);
    combinedWinnerData.push(['Algorithm', 'Fitness', 'Fitness Rank', 'Energy', 'Energy Rank', 'Status']);
    combinedWinner.rankedAlgorithms.forEach((alg) => {
      let status = '';
      if (alg.isPerfectWinner) {
        status = 'Perfect Winner';
      } else if (alg.isBestAlternative) {
        status = 'Best Alternative';
      } else {
        status = '-';
      }
      combinedWinnerData.push([
        alg.name,
        alg.fitness,
        alg.fitnessRank,
        alg.energy,
        alg.energyRank,
        status
      ]);
    });
  } else {
    combinedWinnerData.push(['No combined winner data available.']);
  }
  
  const combinedWinnerSheet = XLSX.utils.aoa_to_sheet(combinedWinnerData);
  XLSX.utils.book_append_sheet(workbook, combinedWinnerSheet, 'Combined Winner');

  // Add box plot sheets if statistics are available
  addBoxPlotSheetsIfAvailable(workbook, results);

  // Write file
  XLSX.writeFile(workbook, filename);
}

/**
 * Exports analysis report to text file
 */
export function exportAnalysisReport(analysisText: string, filename: string = 'analysis_report.txt'): void {
  const blob = new Blob([analysisText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exports statistics results to Excel file
 */
export function exportStatisticsToExcel(results: OptimizationResultsWithStats, filename: string = 'statistics_results.xlsx'): void {
  if (!results.statistics) {
    throw new Error('No statistics available to export');
  }

  const workbook = XLSX.utils.book_new();
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;

  // Helper function to create statistics sheet for a metric
  const createStatisticsSheet = (metricName: string, metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime') => {
    const header: (string | number)[] = ['Algorithm', 'Mean', 'Std Dev', 'Min', 'Max', 'Median'];
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const stats = results.statistics![key][metricKey];
      data.push([
        algorithms[idx],
        stats.mean,
        stats.stdDev,
        stats.min,
        stats.max,
        stats.median,
      ]);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      { wch: 15 }, // Mean
      { wch: 15 }, // Std Dev
      { wch: 15 }, // Min
      { wch: 15 }, // Max
      { wch: 15 }, // Median
    ];

    return sheet;
  };

  // Fitness Statistics Sheet
  const fitnessSheet = createStatisticsSheet('Fitness', 'fitness');
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Statistics');

  // Energy Statistics Sheet
  const energySheet = createStatisticsSheet('Energy', 'energy');
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy Statistics');

  // Cost Statistics Sheet
  const costSheet = createStatisticsSheet('Cost', 'cost');
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Statistics');

  // Response Time Statistics Sheet
  const responseTimeSheet = createStatisticsSheet('Response Time', 'responseTime');
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time Statistics');

  // Execution Time Statistics Sheet
  const executionTimeSheet = createStatisticsSheet('Execution Time', 'executionTime');
  XLSX.utils.book_append_sheet(workbook, executionTimeSheet, 'Execution Time Statistics');

  // ==================== BOX PLOT STATISTICS SHEET ====================
  // Add comprehensive box plot data including quartiles for all metrics
  const createBoxPlotSheet = (metricName: string, metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime') => {
    const header: (string | number)[] = ['Algorithm', 'Min', 'Q1 (25%)', 'Median (50%)', 'Q3 (75%)', 'Max', 'Mean', 'Std Dev', 'IQR', 'Range'];
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const stats = results.statistics![key][metricKey];
      // Calculate quartiles (approximation using available statistics)
      const q1 = (stats.min + stats.median) / 2;
      const q3 = (stats.median + stats.max) / 2;
      const iqr = q3 - q1;
      const range = stats.max - stats.min;
      
      data.push([
        algorithms[idx],
        stats.min,
        q1,
        stats.median,
        q3,
        stats.max,
        stats.mean,
        stats.stdDev,
        iqr,
        range,
      ]);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      { wch: 12 }, // Min
      { wch: 12 }, // Q1
      { wch: 12 }, // Median
      { wch: 12 }, // Q3
      { wch: 12 }, // Max
      { wch: 12 }, // Mean
      { wch: 12 }, // Std Dev
      { wch: 12 }, // IQR
      { wch: 12 }, // Range
    ];

    return sheet;
  };

  // Box Plot Data for Energy (Primary Focus)
  const energyBoxPlotSheet = createBoxPlotSheet('Energy Box Plot', 'energy');
  XLSX.utils.book_append_sheet(workbook, energyBoxPlotSheet, 'Energy Box Plot');

  // Box Plot Data for Fitness
  const fitnessBoxPlotSheet = createBoxPlotSheet('Fitness Box Plot', 'fitness');
  XLSX.utils.book_append_sheet(workbook, fitnessBoxPlotSheet, 'Fitness Box Plot');

  // Box Plot Data for Cost
  const costBoxPlotSheet = createBoxPlotSheet('Cost Box Plot', 'cost');
  XLSX.utils.book_append_sheet(workbook, costBoxPlotSheet, 'Cost Box Plot');

  // Box Plot Data for Response Time
  const responseTimeBoxPlotSheet = createBoxPlotSheet('Response Time Box Plot', 'responseTime');
  XLSX.utils.book_append_sheet(workbook, responseTimeBoxPlotSheet, 'Response Time Box Plot');

  // Composite Score Statistics Sheet
  if (results.compositeScores) {
    const compositeHeader: (string | number)[] = ['Algorithm', 'Median Score', 'Mean Score', 'Std Dev'];
    const compositeData: (string | number)[][] = [compositeHeader];

    algorithmKeys.forEach((key, idx) => {
      const score = results.compositeScores![key];
      if (score) {
        compositeData.push([
          algorithms[idx],
          score.median,
          score.mean,
          score.stdDev,
        ]);
      }
    });

    const compositeSheet = XLSX.utils.aoa_to_sheet(compositeData);
    compositeSheet['!cols'] = [
      { wch: 12 }, // Algorithm
      { wch: 15 }, // Median Score
      { wch: 15 }, // Mean Score
      { wch: 15 }, // Std Dev
    ];
    XLSX.utils.book_append_sheet(workbook, compositeSheet, 'Composite Score Statistics');
  }

  // Pareto Analysis Sheet
  if (results.paretoAnalysis) {
    const paretoHeader: (string | number)[] = ['Algorithm', 'Pareto-Optimal Count', 'Percentage Contribution'];
    const paretoData: (string | number)[][] = [paretoHeader];

    results.paretoAnalysis.algorithmContributions.forEach((contrib) => {
      paretoData.push([
        contrib.algorithmName,
        contrib.paretoOptimalCount,
        contrib.percentageContribution,
      ]);
    });

    const paretoSheet = XLSX.utils.aoa_to_sheet(paretoData);
    paretoSheet['!cols'] = [
      { wch: 15 }, // Algorithm
      { wch: 20 }, // Pareto-Optimal Count
      { wch: 20 }, // Percentage Contribution
    ];
    XLSX.utils.book_append_sheet(workbook, paretoSheet, 'Pareto Analysis');

    // Pareto Front Details Sheet
    const paretoFrontHeader: (string | number)[] = ['Algorithm', 'Run ID', 'Fitness', 'Energy', 'Cost'];
    const paretoFrontData: (string | number)[][] = [paretoFrontHeader];

    results.paretoAnalysis.globalParetoFront.forEach((solution) => {
      paretoFrontData.push([
        solution.algorithmName,
        solution.runId,
        solution.fitness,
        solution.energy,
        solution.cost,
      ]);
    });

    const paretoFrontSheet = XLSX.utils.aoa_to_sheet(paretoFrontData);
    paretoFrontSheet['!cols'] = [
      { wch: 15 }, // Algorithm
      { wch: 10 }, // Run ID
      { wch: 15 }, // Fitness
      { wch: 15 }, // Energy
      { wch: 15 }, // Cost
    ];
    XLSX.utils.book_append_sheet(workbook, paretoFrontSheet, 'Pareto Front Details');
  }

  // Summary Sheet
  const summaryData = [
    ['Summary Information'],
    ['Number of Runs', results.runNumber || 1],
    ['Timestamp', results.timestamp ? new Date(results.timestamp).toLocaleString() : 'N/A'],
    ['Task Count', results.configuration.taskCount],
    ['Property Count', results.configuration.propertyCount],
    ['Initial Population', results.configuration.initialPopulation],
    ['Crossover Rate', results.configuration.crossover],
    ['Mutation Rate', results.configuration.mutation],
    [''],
    ['Sheet Guide'],
    ['1. Fitness Statistics', 'Mean, StdDev, Min, Max, Median for Fitness'],
    ['2. Energy Statistics', 'Mean, StdDev, Min, Max, Median for Energy'],
    ['3. Cost Statistics', 'Mean, StdDev, Min, Max, Median for Cost'],
    ['4. Response Time Statistics', 'Mean, StdDev, Min, Max, Median for Response Time'],
    ['5. Execution Time Statistics', 'Mean, StdDev, Min, Max, Median for Execution Time'],
    ['6. Energy Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ['7. Fitness Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ['8. Cost Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ['9. Response Time Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ...(results.compositeScores ? [['10. Composite Score Statistics', 'Weighted composite scores']] : []),
    ...(results.paretoAnalysis ? [
      ['11. Pareto Analysis', 'Pareto-optimal solution contributions'],
      ['12. Pareto Front Details', 'Individual Pareto-optimal solutions'],
    ] : []),
    [''],
    ['Box Plot Data Explanation'],
    ['Q1 (25%)', 'First Quartile - 25% of values fall below this'],
    ['Median (50%)', 'Second Quartile - middle value (primary comparison metric)'],
    ['Q3 (75%)', 'Third Quartile - 75% of values fall below this'],
    ['IQR', 'Interquartile Range (Q3 - Q1) - measures consistency'],
    ['Range', 'Total spread (Max - Min)'],
    [''],
    ['Interpretation Guide'],
    ['Energy', 'Lower median = Better efficiency, Smaller IQR = More consistent'],
    ['Fitness', 'Higher median = Better quality, Smaller IQR = More consistent'],
    ['Cost', 'Lower median = Better cost, Smaller IQR = More consistent'],
    ['Response Time', 'Lower median = Better performance, Smaller IQR = More consistent'],
    [''],
    ['Note: Statistics use MEDIAN as primary comparison metric'],
    ['Composite Score Weights: Fitness (50%), Energy (30%), Cost (20%)'],
    ...(results.paretoAnalysis ? [
      [''],
      ['Pareto Analysis Summary'],
      ['Total Solutions', results.paretoAnalysis.totalSolutions],
      ['Pareto-Optimal Solutions', results.paretoAnalysis.paretoOptimalSolutions],
      ['Pareto Efficiency', `${((results.paretoAnalysis.paretoOptimalSolutions / results.paretoAnalysis.totalSolutions) * 100).toFixed(2)}%`],
    ] : []),
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [
    { wch: 30 },
    { wch: 50 },
  ];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Write file
  XLSX.writeFile(workbook, filename);
}

/**
 * Exports comparison results to Excel file
 */
export function exportComparisonToExcel(entries: HistoryEntry[], filename: string = 'comparison_results.xlsx'): void {
  if (entries.length === 0) {
    throw new Error('No entries to export');
  }

  const workbook = XLSX.utils.book_new();
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;

  // Helper function to create comparison sheet for a metric
  const createComparisonSheet = (
    metricName: string,
    getValue: (result: OptimizationResults, key: typeof algorithmKeys[number]) => number,
    unit: string = ''
  ) => {
    const header: (string | number)[] = ['Algorithm'];
    entries.forEach((entry, idx) => {
      header.push(`Run ${idx + 1} (${new Date(entry.timestamp).toLocaleDateString()})`);
    });
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const row: (string | number)[] = [algorithms[idx]];
      entries.forEach((entry) => {
        row.push(getValue(entry.results, key));
      });
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      ...entries.map(() => ({ wch: 25 })), // Each run column
    ];

    return sheet;
  };

  // Fitness Comparison Sheet
  const fitnessSheet = createComparisonSheet(
    'Fitness',
    (result, key) => result[key].fitness
  );
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Comparison');

  // Energy Comparison Sheet
  const energySheet = createComparisonSheet(
    'Energy',
    (result, key) => result[key].energy,
    'J'
  );
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy Comparison');

  // Cost Comparison Sheet
  const costSheet = createComparisonSheet(
    'Cost',
    (result, key) => result[key].cost
  );
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Comparison');

  // Response Time Comparison Sheet
  const responseTimeSheet = createComparisonSheet(
    'Response Time',
    (result, key) => result[key].responseTime,
    'ms'
  );
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time Comparison');

  // Configuration Comparison Sheet
  const configHeader: (string | number)[] = ['Parameter'];
  entries.forEach((entry, idx) => {
    configHeader.push(`Run ${idx + 1} (${new Date(entry.timestamp).toLocaleDateString()})`);
  });
  const configData: (string | number)[][] = [
    configHeader,
    ['Task Count', ...entries.map(e => e.results.configuration.taskCount)],
    ['Property Count', ...entries.map(e => e.results.configuration.propertyCount)],
    ['Initial Population', ...entries.map(e => e.results.configuration.initialPopulation)],
    ['Crossover Rate', ...entries.map(e => e.results.configuration.crossover)],
    ['Mutation Rate', ...entries.map(e => e.results.configuration.mutation)],
    ['Number of Runs', ...entries.map(e => {
      const results = e.results as OptimizationResultsWithStats;
      return results.runNumber || 1;
    })],
  ];
  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration Comparison');

  // Add box plot sheets if any entry has statistics
  const entryWithStats = entries.find(e => 'statistics' in e.results && e.results.statistics !== undefined);
  if (entryWithStats) {
    addBoxPlotSheetsIfAvailable(workbook, entryWithStats.results);
  }

  // Write file
  XLSX.writeFile(workbook, filename);
}

export interface CompleteRunResult {
  taskCount: number;
  results: OptimizationResults;
  timestamp: string;
}

/**
 * Exports complete run results to Excel file with comprehensive findings
 */
export function exportCompleteRunToExcel(
  completeRunResults: CompleteRunResult[],
  filename: string = 'complete_run_results.xlsx'
): void {
  if (completeRunResults.length === 0) {
    throw new Error('No results to export');
  }

  const workbook = XLSX.utils.book_new();
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;

  // Helper function to get metric value (uses statistics mean if available, otherwise direct value)
  const getMetricValue = (
    result: OptimizationResults | OptimizationResultsWithStats,
    key: typeof algorithmKeys[number],
    metric: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime'
  ): number => {
    const hasStatistics = 'statistics' in result && result.statistics !== undefined;
    if (hasStatistics && result.statistics) {
      // Use aggregated mean value from statistics
      return result.statistics[key][metric].mean;
    } else {
      // Use single run value
      const algResult = result[key];
      if (metric === 'executionTime') {
        return algResult.executionTime || 0;
      } else if (metric === 'fitness') {
        return algResult.fitness;
      } else if (metric === 'energy') {
        return algResult.energy;
      } else if (metric === 'cost') {
        return algResult.cost;
      } else {
        return algResult.responseTime;
      }
    }
  };

  // Helper function to create a metric sheet across all task counts
  const createMetricSheet = (
    metricName: string,
    metric: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
    unit: string = ''
  ) => {
    const header: (string | number)[] = ['Task Count', ...algorithms];
    const data: (string | number)[][] = [header];

    completeRunResults.forEach((entry) => {
      const row: (string | number)[] = [entry.taskCount];
      algorithmKeys.forEach((key) => {
        row.push(getMetricValue(entry.results, key, metric));
      });
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Task Count
      ...algorithms.map(() => ({ wch: 15 })), // Algorithm columns
    ];

    return sheet;
  };

  // Helper function to create rankings sheet for a metric
  const createRankingsSheet = (
    metricName: string,
    metric: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
    higherIsBetter: boolean = true
  ) => {
    const header: (string | number)[] = ['Task Count', ...algorithms];
    const data: (string | number)[][] = [header];

    completeRunResults.forEach((entry) => {
      const row: (string | number)[] = [entry.taskCount];
      const values = algorithmKeys.map(key => ({
        key,
        value: getMetricValue(entry.results, key, metric)
      }));
      
      // Sort by value (descending for higher is better, ascending for lower is better)
      const sorted = [...values].sort((a, b) => 
        higherIsBetter ? b.value - a.value : a.value - b.value
      );
      
      // Create ranking array
      algorithmKeys.forEach(key => {
        const rank = sorted.findIndex(v => v.key === key) + 1;
        row.push(rank);
      });
      
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    sheet['!cols'] = [
      { wch: 12 },
      ...algorithms.map(() => ({ wch: 12 })),
    ];
    return sheet;
  };

  // ==================== EXISTING METRIC SHEETS ====================
  
  // Fitness Sheet
  const fitnessSheet = createMetricSheet('Fitness', 'fitness');
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness');

  // Energy Sheet
  const energySheet = createMetricSheet('Energy', 'energy', 'J');
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy');

  // Cost Sheet
  const costSheet = createMetricSheet('Cost', 'cost');
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost');

  // Response Time Sheet
  const responseTimeSheet = createMetricSheet('Response Time', 'responseTime', 'ms');
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time');

  // Execution Time Sheet
  const executionTimeSheet = createMetricSheet('Execution Time', 'executionTime', 'ms');
  XLSX.utils.book_append_sheet(workbook, executionTimeSheet, 'Execution Time');

  // ==================== RANKINGS SHEETS ====================
  
  // Fitness Rankings
  const fitnessRankingsSheet = createRankingsSheet('Fitness Rankings', 'fitness', true);
  XLSX.utils.book_append_sheet(workbook, fitnessRankingsSheet, 'Fitness Rankings');

  // Energy Rankings (lower is better)
  const energyRankingsSheet = createRankingsSheet('Energy Rankings', 'energy', false);
  XLSX.utils.book_append_sheet(workbook, energyRankingsSheet, 'Energy Rankings');

  // Cost Rankings (lower is better)
  const costRankingsSheet = createRankingsSheet('Cost Rankings', 'cost', false);
  XLSX.utils.book_append_sheet(workbook, costRankingsSheet, 'Cost Rankings');

  // Response Time Rankings (lower is better)
  const responseTimeRankingsSheet = createRankingsSheet('Response Time Rankings', 'responseTime', false);
  XLSX.utils.book_append_sheet(workbook, responseTimeRankingsSheet, 'Response Rankings');

  // ==================== DETAILED RESULTS SHEET ====================
  // All metrics for all algorithms at all task counts in one comprehensive table
  const detailedResultsHeader: (string | number)[] = [
    'Task Count', 'Algorithm', 'Fitness', 'Energy (J)', 'Cost', 'Response Time (ms)', 'Execution Time (ms)'
  ];
  const detailedResultsData: (string | number)[][] = [detailedResultsHeader];

  completeRunResults.forEach((entry) => {
    algorithmKeys.forEach((key, idx) => {
      detailedResultsData.push([
        entry.taskCount,
        algorithms[idx],
        getMetricValue(entry.results, key, 'fitness'),
        getMetricValue(entry.results, key, 'energy'),
        getMetricValue(entry.results, key, 'cost'),
        getMetricValue(entry.results, key, 'responseTime'),
        getMetricValue(entry.results, key, 'executionTime'),
      ]);
    });
  });

  const detailedResultsSheet = XLSX.utils.aoa_to_sheet(detailedResultsData);
  detailedResultsSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 12 }, // Algorithm
    { wch: 15 }, // Fitness
    { wch: 15 }, // Energy
    { wch: 15 }, // Cost
    { wch: 18 }, // Response Time
    { wch: 18 }, // Execution Time
  ];
  XLSX.utils.book_append_sheet(workbook, detailedResultsSheet, 'Detailed Results');

  // ==================== SOLUTIONS SHEET ====================
  // All solutions for all algorithms at all task counts
  const maxSolutionLength = Math.max(...completeRunResults.map(r => r.results.ga.solution.length));
  const solutionsHeader: (string | number)[] = ['Task Count', 'Algorithm', ...Array.from({ length: maxSolutionLength }, (_, i) => `Task ${i + 1}`)];
  const solutionsData: (string | number)[][] = [solutionsHeader];

  completeRunResults.forEach((entry) => {
    algorithmKeys.forEach((key, idx) => {
      const solution = entry.results[key].solution;
      const row: (string | number)[] = [entry.taskCount, algorithms[idx]];
      // Pad solution array if needed
      for (let i = 0; i < maxSolutionLength; i++) {
        row.push(i < solution.length ? solution[i] : '');
      }
      solutionsData.push(row);
    });
  });

  const solutionsSheet = XLSX.utils.aoa_to_sheet(solutionsData);
  solutionsSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 12 }, // Algorithm
    ...Array(maxSolutionLength).fill({ wch: 8 }), // Solution columns
  ];
  XLSX.utils.book_append_sheet(workbook, solutionsSheet, 'Solutions');

  // ==================== CONFIGURATION SHEET ====================
  const configHeader: (string | number)[] = [
    'Task Count', 'Timestamp', 'Task Count', 'Property Count', 'Initial Population', 'Crossover Rate', 'Mutation Rate'
  ];
  const configData: (string | number)[][] = [configHeader];

  completeRunResults.forEach((entry) => {
    const config = entry.results.configuration;
    configData.push([
      entry.taskCount,
      new Date(entry.timestamp).toLocaleString(),
      config.taskCount,
      config.propertyCount,
      config.initialPopulation,
      config.crossover,
      config.mutation,
    ]);
  });

  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  configSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 22 }, // Timestamp
    { wch: 12 }, // Task Count (config)
    { wch: 15 }, // Property Count
    { wch: 18 }, // Initial Population
    { wch: 15 }, // Crossover Rate
    { wch: 15 }, // Mutation Rate
  ];
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration');

  // ==================== COMBINED WINNERS SHEET ====================
  const combinedWinnersHeader: (string | number)[] = [
    'Task Count', 'Perfect Winner', 'Perfect Winner Fitness', 'Perfect Winner Energy',
    'Best Alternative', 'Best Alt Fitness', 'Best Alt Energy', 'Best Alt Fitness Rank', 'Best Alt Energy Rank'
  ];
  const combinedWinnersData: (string | number)[][] = [combinedWinnersHeader];

  completeRunResults.forEach((entry) => {
    const combinedWinner = calculateCombinedWinner(entry.results);
    if (combinedWinner) {
      if (combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0) {
        combinedWinner.perfectWinners.forEach(winner => {
          combinedWinnersData.push([
            entry.taskCount,
            winner.name,
            winner.fitness,
            winner.energy,
            '-',
            '-',
            '-',
            winner.fitnessRank,
            winner.energyRank,
          ]);
        });
      } else if (combinedWinner.bestAlternative) {
        const alt = combinedWinner.bestAlternative;
        combinedWinnersData.push([
          entry.taskCount,
          '-',
          '-',
          '-',
          alt.name,
          alt.fitness,
          alt.energy,
          alt.fitnessRank,
          alt.energyRank,
        ]);
      } else {
        combinedWinnersData.push([
          entry.taskCount,
          'None',
          '-',
          '-',
          'None',
          '-',
          '-',
          '-',
          '-',
        ]);
      }
    } else {
      combinedWinnersData.push([
        entry.taskCount,
        'N/A',
        '-',
        '-',
        'N/A',
        '-',
        '-',
        '-',
        '-',
      ]);
    }
  });

  const combinedWinnersSheet = XLSX.utils.aoa_to_sheet(combinedWinnersData);
  combinedWinnersSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 18 }, // Perfect Winner
    { wch: 18 }, // Perfect Winner Fitness
    { wch: 18 }, // Perfect Winner Energy
    { wch: 18 }, // Best Alternative
    { wch: 18 }, // Best Alt Fitness
    { wch: 18 }, // Best Alt Energy
    { wch: 18 }, // Best Alt Fitness Rank
    { wch: 18 }, // Best Alt Energy Rank
  ];
  XLSX.utils.book_append_sheet(workbook, combinedWinnersSheet, 'Combined Winners');

  // ==================== COMPREHENSIVE RANKINGS SHEET ====================
  // Shows all rankings for all metrics at each task count
  const comprehensiveRankingsHeader: (string | number)[] = [
    'Task Count', 'Algorithm', 'Fitness Rank', 'Energy Rank', 'Cost Rank', 'Response Time Rank'
  ];
  const comprehensiveRankingsData: (string | number)[][] = [comprehensiveRankingsHeader];

  completeRunResults.forEach((entry) => {
    // Calculate rankings for each metric (using aggregated values when available)
    const fitnessRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'fitness') }))
      .sort((a, b) => b.value - a.value);
    const energyRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'energy') }))
      .sort((a, b) => a.value - b.value);
    const costRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'cost') }))
      .sort((a, b) => a.value - b.value);
    const responseTimeRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'responseTime') }))
      .sort((a, b) => a.value - b.value);

    algorithmKeys.forEach((key, idx) => {
      const fitnessRank = fitnessRanked.findIndex(r => r.key === key) + 1;
      const energyRank = energyRanked.findIndex(r => r.key === key) + 1;
      const costRank = costRanked.findIndex(r => r.key === key) + 1;
      const responseRank = responseTimeRanked.findIndex(r => r.key === key) + 1;

      comprehensiveRankingsData.push([
        entry.taskCount,
        algorithms[idx],
        fitnessRank,
        energyRank,
        costRank,
        responseRank,
      ]);
    });
  });

  const comprehensiveRankingsSheet = XLSX.utils.aoa_to_sheet(comprehensiveRankingsData);
  comprehensiveRankingsSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 12 }, // Algorithm
    { wch: 14 }, // Fitness Rank
    { wch: 14 }, // Energy Rank
    { wch: 12 }, // Cost Rank
    { wch: 18 }, // Response Time Rank
  ];
  XLSX.utils.book_append_sheet(workbook, comprehensiveRankingsSheet, 'All Rankings');

  // ==================== FINDINGS & ANALYSIS SHEET ====================
  const findingsData: (string | number)[][] = [];
  
  // Header
  findingsData.push(['COMPLETE RUN FINDINGS & ANALYSIS']);
  findingsData.push([]);
  
  // Summary Information
  findingsData.push(['SUMMARY INFORMATION']);
  findingsData.push(['Total Runs', completeRunResults.length]);
  findingsData.push(['Start Date', new Date(completeRunResults[0].timestamp).toLocaleString()]);
  findingsData.push(['End Date', new Date(completeRunResults[completeRunResults.length - 1].timestamp).toLocaleString()]);
  findingsData.push(['Task Count Range', `${Math.min(...completeRunResults.map(r => r.taskCount))} - ${Math.max(...completeRunResults.map(r => r.taskCount))}`]);
  findingsData.push([]);
  
  // Best Performers Analysis
  findingsData.push(['BEST PERFORMERS ANALYSIS']);
  findingsData.push([]);
  
  // Count wins for each algorithm across all metrics and task counts
  const winCounts: { [key: string]: { fitness: number; energy: number; cost: number; responseTime: number; total: number } } = {};
  algorithms.forEach(alg => {
    winCounts[alg] = { fitness: 0, energy: 0, cost: 0, responseTime: 0, total: 0 };
  });

  completeRunResults.forEach((entry) => {
    // Fitness winners (higher is better) - using aggregated values
    const fitnessSorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'fitness') }))
      .sort((a, b) => b.value - a.value);
    if (fitnessSorted.length > 0) winCounts[fitnessSorted[0].name].fitness++;
    
    // Energy winners (lower is better) - using aggregated values
    const energySorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'energy') }))
      .sort((a, b) => a.value - b.value);
    if (energySorted.length > 0) winCounts[energySorted[0].name].energy++;
    
    // Cost winners (lower is better) - using aggregated values
    const costSorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'cost') }))
      .sort((a, b) => a.value - b.value);
    if (costSorted.length > 0) winCounts[costSorted[0].name].cost++;
    
    // Response Time winners (lower is better) - using aggregated values
    const responseTimeSorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'responseTime') }))
      .sort((a, b) => a.value - b.value);
    if (responseTimeSorted.length > 0) winCounts[responseTimeSorted[0].name].responseTime++;
  });

  // Calculate total wins
  algorithms.forEach(alg => {
    winCounts[alg].total = winCounts[alg].fitness + winCounts[alg].energy + winCounts[alg].cost + winCounts[alg].responseTime;
  });

  findingsData.push(['Algorithm', 'Fitness Wins', 'Energy Wins', 'Cost Wins', 'Response Time Wins', 'Total Wins']);
  algorithms.forEach(alg => {
    const counts = winCounts[alg];
    findingsData.push([alg, counts.fitness, counts.energy, counts.cost, counts.responseTime, counts.total]);
  });
  findingsData.push([]);

  // Combined Winner Analysis
  findingsData.push(['COMBINED WINNER ANALYSIS']);
  let perfectWinnerCount = 0;
  const bestAlternativeCounts: { [key: string]: number } = {};
  algorithms.forEach(alg => bestAlternativeCounts[alg] = 0);

  completeRunResults.forEach((entry) => {
    const combinedWinner = calculateCombinedWinner(entry.results);
    if (combinedWinner) {
      if (combinedWinner.hasPerfectWinner) {
        perfectWinnerCount += combinedWinner.perfectWinners.length;
      }
      if (combinedWinner.bestAlternative) {
        bestAlternativeCounts[combinedWinner.bestAlternative.name]++;
      }
    }
  });

  findingsData.push(['Perfect Winners (Total Occurrences)', perfectWinnerCount]);
  findingsData.push(['Best Alternative Counts:']);
  algorithms.forEach(alg => {
    if (bestAlternativeCounts[alg] > 0) {
      findingsData.push(['', `${alg}: ${bestAlternativeCounts[alg]}`]);
    }
  });
  findingsData.push([]);

  // Best Overall Algorithm
  findingsData.push(['BEST OVERALL ALGORITHM']);
  const sortedByTotalWins = algorithms.map(alg => ({ name: alg, total: winCounts[alg].total }))
    .sort((a, b) => b.total - a.total);
  if (sortedByTotalWins.length > 0) {
    findingsData.push(['Algorithm with Most Total Wins', sortedByTotalWins[0].name]);
    findingsData.push(['Total Wins', sortedByTotalWins[0].total]);
    findingsData.push([]);
    findingsData.push(['Overall Ranking by Total Wins:']);
    sortedByTotalWins.forEach((alg, idx) => {
      findingsData.push([`${idx + 1}.`, alg.name, `${alg.total} wins`]);
    });
  }
  findingsData.push([]);

  // Performance Trends
  findingsData.push(['PERFORMANCE TRENDS']);
  findingsData.push(['(See individual metric sheets for detailed trends)']);
  findingsData.push([]);

  // Key Findings Summary
  findingsData.push(['KEY FINDINGS']);
  findingsData.push(['1. Best Fitness Algorithm', sortedByTotalWins.length > 0 ? algorithms.reduce((best, alg) => winCounts[alg].fitness > winCounts[best].fitness ? alg : best) : 'N/A']);
  findingsData.push(['2. Best Energy Algorithm', algorithms.reduce((best, alg) => winCounts[alg].energy > winCounts[best].energy ? alg : best)]);
  findingsData.push(['3. Best Cost Algorithm', algorithms.reduce((best, alg) => winCounts[alg].cost > winCounts[best].cost ? alg : best)]);
  findingsData.push(['4. Best Response Time Algorithm', algorithms.reduce((best, alg) => winCounts[alg].responseTime > winCounts[best].responseTime ? alg : best)]);
  findingsData.push(['5. Most Consistent Performer', sortedByTotalWins.length > 0 ? sortedByTotalWins[0].name : 'N/A']);
  findingsData.push(['6. Perfect Winners Found', perfectWinnerCount]);
  findingsData.push([]);
  findingsData.push(['Note: Detailed metric values, rankings, solutions, and configurations are available in other sheets.']);

  const findingsSheet = XLSX.utils.aoa_to_sheet(findingsData);
  findingsSheet['!cols'] = [
    { wch: 30 }, // First column
    { wch: 20 }, // Second column
    { wch: 20 }, // Third column
    { wch: 20 }, // Fourth column
    { wch: 20 }, // Fifth column
    { wch: 15 }, // Sixth column
  ];
  XLSX.utils.book_append_sheet(workbook, findingsSheet, 'Findings & Analysis');

  // ==================== ENHANCED SUMMARY SHEET ====================
  const summaryData = [
    ['Complete Run Summary'],
    ['Total Runs', completeRunResults.length],
    ['Start Date', new Date(completeRunResults[0].timestamp).toLocaleString()],
    ['End Date', new Date(completeRunResults[completeRunResults.length - 1].timestamp).toLocaleString()],
    [''],
    ['Task Count Range'],
    ['Min', Math.min(...completeRunResults.map(r => r.taskCount))],
    ['Max', Math.max(...completeRunResults.map(r => r.taskCount))],
    [''],
    ['Task Counts'],
    ...completeRunResults.map(r => [r.taskCount]),
    [''],
    ['Sheet Overview'],
    ['1. Summary', 'Overview of the complete run'],
    ['2. Fitness', 'Fitness values across task counts'],
    ['3. Energy', 'Energy consumption across task counts'],
    ['4. Cost', 'Cost values across task counts'],
    ['5. Response Time', 'Response time across task counts'],
    ['6. Execution Time', 'Execution time across task counts'],
    ['7. Fitness Rankings', 'Rankings for fitness metric'],
    ['8. Energy Rankings', 'Rankings for energy metric'],
    ['9. Cost Rankings', 'Rankings for cost metric'],
    ['10. Response Rankings', 'Rankings for response time metric'],
    ['11. Detailed Results', 'All metrics for all algorithms'],
    ['12. Solutions', 'Service selection solutions'],
    ['13. Configuration', 'Parameters used for each run'],
    ['14. Combined Winners', 'Perfect winners and best alternatives'],
    ['15. All Rankings', 'Comprehensive ranking matrix'],
    ['16. Findings & Analysis', 'Key findings and analysis'],
    ['17. Energy Box Plot', 'Box plot statistics for Energy (if available)'],
    ['18. Fitness Box Plot', 'Box plot statistics for Fitness (if available)'],
    ['19. Cost Box Plot', 'Box plot statistics for Cost (if available)'],
    ['20. Response Time Box Plot', 'Box plot statistics for Response Time (if available)'],
    [''],
    ['Note: Box plot sheets (17-20) are only included if numberOfRuns > 1'],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [
    { wch: 25 },
    { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Add box plot sheets if any result has statistics
  const resultWithStats = completeRunResults.find(r => 'statistics' in r.results && r.results.statistics !== undefined);
  if (resultWithStats) {
    addBoxPlotSheetsIfAvailable(workbook, resultWithStats.results);
  }

  // Write file
  XLSX.writeFile(workbook, filename);
}
